import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { 
  Users, 
  Crown,
  Shield,
  UserCog,
  UserCircle,
  ChevronRight,
  Link2,
  Unlink,
  Target,
  CheckSquare
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roleIcons = {
  owner: Crown,
  admin: Shield,
  sales_manager: UserCog,
  sales_rep: UserCircle
};

const roleColors = {
  owner: "bg-amber-100 text-amber-700",
  admin: "bg-purple-100 text-purple-700",
  sales_manager: "bg-blue-100 text-blue-700",
  sales_rep: "bg-slate-100 text-slate-600"
};

export default function TeamHierarchyPage({ user }) {
  const [hierarchy, setHierarchy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [selectedRep, setSelectedRep] = useState(null);
  const [selectedManager, setSelectedManager] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState("");

  useEffect(() => {
    fetchHierarchy();
  }, []);

  const fetchHierarchy = async () => {
    try {
      const response = await fetch(`${API}/team/hierarchy`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setHierarchy(data);
      } else if (response.status === 403) {
        setHierarchy(null);
      }
    } catch (error) {
      console.error("Failed to fetch hierarchy:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignManager = async () => {
    if (!selectedRep || !selectedManager) return;

    try {
      const response = await fetch(`${API}/team/assign-manager`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          manager_id: selectedManager,
          sales_rep_id: selectedRep.user_id
        })
      });

      if (response.ok) {
        toast.success("Manager assigned successfully");
        setAssignDialogOpen(false);
        setSelectedRep(null);
        setSelectedManager("");
        fetchHierarchy();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to assign manager");
      }
    } catch (error) {
      toast.error("Failed to assign manager");
    }
  };

  const handleUnassignManager = async (repId) => {
    if (!window.confirm("Remove manager assignment for this sales rep?")) return;

    try {
      const response = await fetch(`${API}/team/unassign-manager`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ sales_rep_id: repId })
      });

      if (response.ok) {
        toast.success("Manager assignment removed");
        fetchHierarchy();
      } else {
        toast.error("Failed to remove assignment");
      }
    } catch (error) {
      toast.error("Failed to remove assignment");
    }
  };

  const handleChangeRole = async () => {
    if (!selectedUser || !newRole) return;

    try {
      const response = await fetch(`${API}/team/members/${selectedUser.user_id}/role`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ role: newRole })
      });

      if (response.ok) {
        toast.success("Role updated successfully");
        setRoleDialogOpen(false);
        setSelectedUser(null);
        setNewRole("");
        fetchHierarchy();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to update role");
      }
    } catch (error) {
      toast.error("Failed to update role");
    }
  };

  const openAssignDialog = (rep) => {
    setSelectedRep(rep);
    setSelectedManager(rep.manager_id || "");
    setAssignDialogOpen(true);
  };

  const openRoleDialog = (member) => {
    setSelectedUser(member);
    setNewRole(member.role);
    setRoleDialogOpen(true);
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const getManagerName = (managerId) => {
    if (!managerId || !hierarchy) return null;
    const manager = hierarchy.managers.find(m => m.user_id === managerId);
    return manager?.name;
  };

  const canManageTeam = user?.role === "owner" || user?.role === "admin";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!hierarchy) {
    return (
      <div className="text-center py-12">
        <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
        <h2 className="text-xl font-medium text-slate-900 mb-2">Access Restricted</h2>
        <p className="text-slate-500">Only owners, admins, and managers can view the team hierarchy.</p>
      </div>
    );
  }

  const MemberCard = ({ member, showActions = false }) => {
    const Icon = roleIcons[member.role] || UserCircle;
    
    return (
      <div className="flex items-center justify-between p-3 bg-white rounded-lg border shadow-sm">
        <div className="flex items-center gap-3">
          <Avatar>
            <AvatarImage src={member.picture} />
            <AvatarFallback>{getInitials(member.name)}</AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium">{member.name}</p>
            <p className="text-xs text-slate-500">{member.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={roleColors[member.role]}>
            <Icon className="w-3 h-3 mr-1" />
            {member.role?.replace("_", " ")}
          </Badge>
          {showActions && canManageTeam && member.role !== "owner" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openRoleDialog(member)}
              data-testid={`edit-role-${member.user_id}`}
            >
              Edit
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="team-hierarchy-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">
          Team Hierarchy
        </h1>
        <p className="text-slate-500 mt-1">
          View and manage your organization's team structure
        </p>
      </div>

      {/* Hierarchy Visualization */}
      <div className="grid gap-6">
        {/* Owners */}
        {hierarchy.owners.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Crown className="w-5 h-5 text-amber-500" />
                Account Owners
              </CardTitle>
              <CardDescription>Full control of the organization</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {hierarchy.owners.map((owner) => (
                <MemberCard key={owner.user_id} member={owner} />
              ))}
            </CardContent>
          </Card>
        )}

        {/* Admins */}
        {hierarchy.admins.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-500" />
                Administrators
              </CardTitle>
              <CardDescription>Manage team and settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {hierarchy.admins.map((admin) => (
                <MemberCard key={admin.user_id} member={admin} showActions />
              ))}
            </CardContent>
          </Card>
        )}

        {/* Managers with their teams */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <UserCog className="w-5 h-5 text-blue-500" />
              Sales Managers & Teams
            </CardTitle>
            <CardDescription>Managers and their assigned sales reps</CardDescription>
          </CardHeader>
          <CardContent>
            {hierarchy.managers.length === 0 ? (
              <p className="text-slate-500 text-center py-4">No managers assigned yet</p>
            ) : (
              <div className="space-y-6">
                {hierarchy.managers.map((manager) => {
                  const teamReps = hierarchy.sales_reps.filter(
                    rep => rep.manager_id === manager.user_id
                  );
                  
                  return (
                    <div key={manager.user_id} className="space-y-3">
                      <div className="flex items-center justify-between">
                        <MemberCard member={manager} showActions />
                      </div>
                      
                      {teamReps.length > 0 && (
                        <div className="ml-8 pl-4 border-l-2 border-slate-200 space-y-2">
                          {teamReps.map((rep) => (
                            <div key={rep.user_id} className="flex items-center gap-2">
                              <ChevronRight className="w-4 h-4 text-slate-300" />
                              <div className="flex-1 flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                                <div className="flex items-center gap-2">
                                  <Avatar className="h-8 w-8">
                                    <AvatarImage src={rep.picture} />
                                    <AvatarFallback className="text-xs">
                                      {getInitials(rep.name)}
                                    </AvatarFallback>
                                  </Avatar>
                                  <span className="text-sm">{rep.name}</span>
                                </div>
                                {canManageTeam && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleUnassignManager(rep.user_id)}
                                    className="text-slate-500 hover:text-red-600"
                                    data-testid={`unassign-${rep.user_id}`}
                                  >
                                    <Unlink className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Unassigned Sales Reps */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <UserCircle className="w-5 h-5 text-slate-500" />
              Unassigned Sales Reps
            </CardTitle>
            <CardDescription>Sales reps without a manager</CardDescription>
          </CardHeader>
          <CardContent>
            {hierarchy.sales_reps.filter(r => !r.manager_id).length === 0 ? (
              <p className="text-slate-500 text-center py-4">All sales reps are assigned to managers</p>
            ) : (
              <div className="space-y-2">
                {hierarchy.sales_reps.filter(r => !r.manager_id).map((rep) => (
                  <div key={rep.user_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Avatar>
                        <AvatarImage src={rep.picture} />
                        <AvatarFallback>{getInitials(rep.name)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{rep.name}</p>
                        <p className="text-xs text-slate-500">{rep.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {canManageTeam && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openAssignDialog(rep)}
                            data-testid={`assign-manager-${rep.user_id}`}
                          >
                            <Link2 className="w-4 h-4 mr-1" />
                            Assign Manager
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openRoleDialog(rep)}
                          >
                            Edit Role
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Assign Manager Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Manager</DialogTitle>
            <DialogDescription>
              Select a manager for {selectedRep?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Select value={selectedManager} onValueChange={setSelectedManager}>
              <SelectTrigger data-testid="select-manager">
                <SelectValue placeholder="Select a manager" />
              </SelectTrigger>
              <SelectContent>
                {hierarchy.managers.map((manager) => (
                  <SelectItem key={manager.user_id} value={manager.user_id}>
                    {manager.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {hierarchy.managers.length === 0 && (
              <p className="text-sm text-slate-500 mt-2">
                No managers available. Promote a team member to Sales Manager first.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAssignManager}
              disabled={!selectedManager}
              data-testid="confirm-assign"
            >
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Role Dialog */}
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Role</DialogTitle>
            <DialogDescription>
              Update role for {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Select value={newRole} onValueChange={setNewRole}>
              <SelectTrigger data-testid="select-role">
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Administrator</SelectItem>
                <SelectItem value="sales_manager">Sales Manager</SelectItem>
                <SelectItem value="sales_rep">Sales Rep</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleChangeRole}
              disabled={!newRole || newRole === selectedUser?.role}
              data-testid="confirm-role-change"
            >
              Update Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
